/**
 * Panel lateral.
 *
 * El anillo de tiempo pasa a ámbar después de 75 minutos. No es una alarma:
 * es la señal de que esa mesa probablemente ya terminó y conviene pasar por
 * ahí. En un restaurante lleno, rotar mesa es plata.
 *
 * El indicador de conexión es deliberadamente discreto. Estar sin señal es
 * normal en este sistema, no una falla — lo que importa es que se vea cuántas
 * operaciones faltan por subir.
 */

import { cop, duracion, pad2 } from '../lib/formato';
import type { PagoLocal } from '../datos/db';
import type { MesaVista } from '../datos/vistas';
import type { RespuestaTurnoActual, Usuario } from '../tipos/contrato';

interface Props {
  usuario: Usuario | null;
  mesaActual: MesaVista | null;
  /**
   * Minutos de la cuenta seleccionada. Con varias cuentas en el mismo puesto,
   * el anillo tiene que medir la que el mesero está mirando, no la más vieja
   * de la mesa.
   */
  minutosCuenta: number | null;
  mesas: MesaVista[];
  cobros: PagoLocal[];
  enLinea: boolean;
  pendientes: number;
  turno: RespuestaTurnoActual | null;
  onDescargarRecibo: (pago: PagoLocal) => void;
  onCerrarCaja: () => void;
}

const CIRCUNFERENCIA = 364; // 2πr con r = 58

export function PanelLateral({
  usuario,
  mesaActual,
  minutosCuenta,
  mesas,
  cobros,
  enLinea,
  pendientes,
  turno,
  onDescargarRecibo,
  onCerrarCaja,
}: Props) {
  const minutos = minutosCuenta ?? mesaActual?.minutos ?? 0;
  const proporcion = Math.min(minutos / 90, 1);
  const alerta = minutos > 75;

  // Los totales del turno los manda el servidor, que ve los cobros de todos
  // los dispositivos. Los locales solo se usan mientras no haya sincronizado:
  // una tablet sola nunca debería mostrar cifras de caja incompletas como si
  // fueran definitivas.
  const resumen = turno?.resumen;
  console.log('resumen', resumen);
  const ventas = resumen ? Number(resumen.totalVendido) : cobros.reduce((s, c) => s + c.total, 0);
  const cobradas = resumen ? resumen.ordenesPagadas : cobros.length;
  const iniciales = (usuario?.nombre ?? '· ·')
    .split(' ')
    .map((p) => p[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      <section className="card user">
        <div className="avatar">{iniciales}</div>
        <div style={{ flex: 1 }}>
          <div className="n">{usuario?.nombre ?? 'Sin sesión'}</div>
          <div className="r" style={{ textTransform: 'capitalize' }}>
            {usuario?.rol ?? '—'}
          </div>
        </div>
        <div className={`conexion${enLinea ? '' : ' offline'}`} title={enLinea ? 'Conectado' : 'Trabajando sin conexión'}>
          <i />
          {enLinea ? 'En línea' : 'Offline'}
        </div>
      </section>

      <section className="card timer">
        <div className="eyebrow">Tiempo en mesa</div>
        <div className="ring">
          <svg width="132" height="132" viewBox="0 0 132 132">
            <circle cx="66" cy="66" r="58" fill="none" stroke="var(--bg-2)" strokeWidth="9" />
            <circle
              cx="66"
              cy="66"
              r="58"
              fill="none"
              stroke={alerta ? 'var(--accent)' : 'var(--primary)'}
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={CIRCUNFERENCIA}
              strokeDashoffset={CIRCUNFERENCIA - CIRCUNFERENCIA * proporcion}
            />
          </svg>
          <div className="val mono">
            {mesaActual ? duracion(minutos) : '--:--'}
            <small>{mesaActual ? `MESA ${pad2(mesaActual.numero)}` : 'SIN MESA'}</small>
          </div>
        </div>
      </section>

      <section className="card charges" style={{ flex: 1 }}>
        <div
          className="eyebrow"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span>{turno?.turno ? `Turno #${turno.turno.numero}` : 'Resumen del turno'}</span>
          {turno?.turno && (
            <button
              onClick={onCerrarCaja}
              style={{
                fontSize: 10,
                letterSpacing: '.1em',
                fontWeight: 700,
                color: 'var(--primary-700)',
              }}
            >
              CERRAR CAJA
            </button>
          )}
        </div>
        <div className="stats" style={{ paddingTop: 0 }}>
          <div className="stat">
            <span>Mesas abiertas</span>
            <b className="mono">{mesas.filter((m) => m.estado !== 'libre').length}</b>
          </div>
          <div className="stat">
            <span>Cobros realizados</span>
            <b className="mono">{cobradas}</b>
          </div>
          <div className="stat">
            <span>Ventas del día</span>
            <b className="mono">{cop(ventas)}</b>
          </div>
          {resumen && (
            <div className="stat">
              <span>Efectivo esperado</span>
              <b className="mono">{cop(resumen.efectivoEsperado)}</b>
            </div>
          )}
          {pendientes > 0 && (
            <div className="stat">
              <span>Sin sincronizar</span>
              <span className="pendientes">{pendientes}</span>
            </div>
          )}
        </div>

        <div
          className="eyebrow"
          style={{ paddingTop: 12, borderTop: '1px solid var(--line)', marginTop: 4 }}
        >
          Últimos cobros
        </div>

        {cobros.length === 0 ? (
          <div className="none">
            Aún no hay cobros en este turno. Cada mesa cerrada aparece acá con su recibo en PDF.
          </div>
        ) : (
          <ul>
            {cobros.map((c) => (
              <li key={c.id}>
                <div className="who">
                  <b>
                    {c.mesaNumero ? `Mesa ${pad2(c.mesaNumero)}` : 'Para llevar'} · #{c.ordenNumero}
                  </b>
                  <span>
                    {c.detalle ?? c.metodo} · {new Date(c.creadoEn).toLocaleTimeString('es-CO', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    })}
                  </span>
                </div>
                <span className="amt mono">{cop(c.total)}</span>
                <button
                  className="dl"
                  title="Descargar recibo"
                  onClick={() => onDescargarRecibo(c)}
                >
                  ⭳
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
